import { useCallback, useState } from "react";

import { reinviteAgentInvitation } from "@/api/directoryApi";

/**
 * Resend a workspace invitation for an expired invite or revoked agent.
 *
 * @param {{
 *   email: string,
 *   role?: string,
 *   workspaceId?: string | null,
 *   onDone?: () => void | Promise<void>,
 * }} props
 */
export default function ReinviteAgentButton({ email, role = "chat", workspaceId = null, onDone }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  const handleClick = useCallback(async () => {
    setBusy(true);
    setErr("");
    setOk(false);
    try {
      await reinviteAgentInvitation({ email, role, workspaceId });
      setOk(true);
      await onDone?.();
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "Could not reinvite this agent.");
    } finally {
      setBusy(false);
    }
  }, [email, role, workspaceId, onDone]);

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        disabled={busy}
        onClick={() => void handleClick()}
        className="rounded-lg border border-teal-200 bg-teal-50 px-3 py-1.5 text-xs font-semibold text-teal-800 transition hover:bg-teal-100 disabled:opacity-60 dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-200"
      >
        {busy ? "Sending…" : ok ? "Invitation sent" : "Reinvite"}
      </button>
      {err ? (
        <p className="max-w-[12rem] text-right text-[10px] leading-snug text-rose-600 dark:text-rose-400" role="alert">
          {err}
        </p>
      ) : null}
    </div>
  );
}
